<script setup lang="ts">
    import TickSlider from '@/views/3d-planner/components/TickSlider.vue';
    import PlannerSlider from '@/views/3d-planner/components/PlannerSlider.vue';
    import { useCupOverlayViewModel } from '@/views/3d-planner/cup/useCupOverlayViewModel';
    import { ref } from 'vue';
    import GroupLabel from '@/views/3d-planner/components/GroupLabel.vue';
    import GroupSubtitle from '@/views/3d-planner/components/GroupSubtitle.vue';
    import CupPositionControls from '@/views/3d-planner/cup/components/CupPositionControls.vue';
    import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
    import HipSpineXRayModal from '@/views/3d-planner/hipSpine/components/HipSpineXRayModal.vue';
    import HipSpineSafeZoneSlider from '@/views/3d-planner/hipSpine/components/HipSpineSafeZoneSlider.vue';
    import { useHipSpineAnalysisViewModel } from '@/views/3d-planner/hipSpine/useHipSpineAnalysisViewModel';
    import StandingAnteversionPromptModal from '@/views/3d-planner/hipSpine/components/StandingAnteversionPromptModal.vue';
    import HipSpineWarningsPanel from '@/views/3d-planner/hipSpine/components/HipSpineWarningsPanel.vue';
    import { HIP_SPINE_TARGETS, PLANNER_TARGETS } from '@/domain/analytics/analyticsTargets';
    import {
        MAX_ANTEVERSION,
        MAX_INCLINATION,
        MIN_ANTEVERSION,
        MIN_INCLINATION,
    } from '@/domain/anatomy/pelvis/anteversionInclination';

    const vm = useCupOverlayViewModel();

    const hipSpineStore = useHipSpineStore();
    const hipSpineAnalysis = useHipSpineAnalysisViewModel();
    const viewXRays = ref<boolean>(false);
    const safeZoneOptions = [
        { label: 'Show Safe Zone', value: 'show' },
        { label: 'Hide Safe Zone', value: 'hide' },
    ];

    const closeModal = () => {
        viewXRays.value = false;
    };

    const openModal = () => {
        viewXRays.value = true;
    };
</script>

<template>
    <div class="expansion-panel d-flex flex-column bg-surface-light">
        <div class="cup-panel d-flex flex-column ga-4" data-testid="cup-panel">
            <div>
                <div class="d-flex justify-space-between align-center">
                    <group-label>Cup Size</group-label>
                    <div class="text-paragraph-sm">{{ vm.selectedCupSize }}</div>
                </div>
                <tick-slider
                    v-model="vm.selectedCupIndex"
                    :data-analytics-id="PLANNER_TARGETS.cupSizeAdjust"
                    class="pb-3"
                    :tick-count="vm.availableCups.length"
                />
            </div>

            <div
                v-if="vm.dualMobilityVisible"
                class="pb-3 d-flex justify-space-between align-center"
            >
                <group-subtitle>Dual Mobility</group-subtitle>
                <v-switch
                    id="dual-mobility"
                    v-model="vm.dualMobility"
                    class="app-switch"
                    density="comfortable"
                    color="success"
                    inset
                    base-color="rgb(var(--v-theme-grey-light-1))"
                    hide-details
                    :disabled="vm.dualMobilityDisabled"
                />
            </div>

            <div
                class="pb-3 border-sm rounded-lg pa-4 pt-1"
                :data-analytics-id="PLANNER_TARGETS.cupAdjustments"
            >
                <group-label class="input-group mt-2">Cup Adjustments</group-label>

                <template
                    v-if="
                        hipSpineAnalysis.isSafeZoneSliderMode &&
                        hipSpineAnalysis.featureEnabled &&
                        hipSpineAnalysis.canShowSafeZoneControls &&
                        vm.anteversion !== undefined &&
                        vm.inclination !== undefined
                    "
                >
                    <div class="input-group mt-2">
                        <hip-spine-safe-zone-slider
                            :data-analytics-id="HIP_SPINE_TARGETS.anteversionSlider"
                            :model-value="vm.anteversion"
                            :min="MIN_ANTEVERSION"
                            :max="MAX_ANTEVERSION"
                            label="Anteversion (degrees)"
                            :safe-band="hipSpineAnalysis.anteversionSafeBand"
                            :surgeon-bounds="hipSpineAnalysis.surgeonAnteversionBounds"
                            :combined-version-mark="
                                hipSpineAnalysis.combinedVersionAtAnteversionAxis
                            "
                            :is-out-of-safe-zone="hipSpineAnalysis.isCupOutsideSafeZone"
                            @update:model-value="vm.anteversion = $event"
                        />
                    </div>

                    <div class="input-group">
                        <hip-spine-safe-zone-slider
                            :data-analytics-id="HIP_SPINE_TARGETS.inclinationSlider"
                            :model-value="vm.inclination"
                            :min="MIN_INCLINATION"
                            :max="MAX_INCLINATION"
                            label="Inclination (degrees)"
                            :safe-band="hipSpineAnalysis.inclinationSafeBand"
                            :surgeon-bounds="hipSpineAnalysis.surgeonInclinationBounds"
                            :combined-version-mark="
                                hipSpineAnalysis.combinedVersionAtInclinationAxis
                            "
                            :is-out-of-safe-zone="hipSpineAnalysis.isCupOutsideSafeZone"
                            @update:model-value="vm.inclination = $event"
                        />
                    </div>
                </template>
                <template v-else>
                    <div class="input-group mt-2">
                        <group-subtitle>Anteversion (degrees)</group-subtitle>
                        <planner-slider
                            v-model="vm.anteversion"
                            :min="MIN_ANTEVERSION"
                            :max="MAX_ANTEVERSION"
                        />
                    </div>

                    <div class="input-group">
                        <group-subtitle>Inclination (degrees)</group-subtitle>
                        <planner-slider
                            v-model="vm.inclination"
                            :min="MIN_INCLINATION"
                            :max="MAX_INCLINATION"
                        />
                    </div>
                </template>

                <div
                    v-if="
                        hipSpineAnalysis.featureEnabled && hipSpineAnalysis.canShowSafeZoneControls
                    "
                    class="d-flex justify-space-between align-center input-group mt-5"
                >
                    <group-subtitle>Hip Spine View</group-subtitle>
                    <v-switch
                        id="hip-spine-safe-zone-slider-mode"
                        v-model="hipSpineAnalysis.isSafeZoneSliderMode"
                        data-testid="hip-spine-safe-zone-slider-mode"
                        :data-analytics-id="HIP_SPINE_TARGETS.hipSpineToggle"
                        class="app-switch"
                        density="comfortable"
                        color="success"
                        inset
                        base-color="rgb(var(--v-theme-grey-light-1))"
                        hide-details
                    />
                </div>

                <template v-if="hipSpineAnalysis.featureEnabled">
                    <div class="hip-spine-control-panel my-2">
                        <v-alert
                            v-if="!hipSpineStore.hasStudy"
                            variant="tonal"
                            icon="mdi-alert-circle-outline"
                            type="info"
                            text="Upload x-rays to enable Hip-Spine Analysis"
                            class="text-subtitle-2 font-weight-bold"
                        />
                        <v-alert
                            v-else-if="hipSpineStore.isStudyProcessing"
                            variant="tonal"
                            type="info"
                            class="text-subtitle-2 font-weight-bold"
                        >
                            <template #prepend>
                                <v-progress-circular indeterminate size="20" width="2" />
                            </template>
                            Hip-Spine Analysis in progress
                        </v-alert>
                        <v-alert
                            v-else-if="hipSpineStore.isStudyError"
                            variant="tonal"
                            type="error"
                            text="Hip-Spine Analysis failed, please upload x-rays to try again."
                            class="text-subtitle-2 font-weight-bold"
                        />
                        <div v-else class="d-flex flex-column ga-4 pt-3">
                            <app-button-secondary
                                class="w-100"
                                :data-analytics-id="HIP_SPINE_TARGETS.hipSpineButton"
                                :prepend-icon="
                                    hipSpineAnalysis.isHipSpineApproved ? 'mdi-check' : undefined
                                "
                                @click="openModal"
                            >
                                {{
                                    hipSpineAnalysis.isHipSpineApproved
                                        ? 'View Approved Hip-Spine'
                                        : 'Review Hip-Spine'
                                }}
                            </app-button-secondary>

                            <app-toggle
                                v-if="hipSpineAnalysis.canShowSafeZoneControls"
                                v-model="hipSpineStore.safeZoneVisibility"
                                :data-analytics-id="HIP_SPINE_TARGETS.safeZoneToggle"
                                :options="safeZoneOptions"
                            />

                            <hip-spine-warnings-panel
                                v-if="hipSpineAnalysis.showWarningsPanel"
                                :data-analytics-id="HIP_SPINE_TARGETS.hipSpineWarnings"
                                :classification="hipSpineAnalysis.classification!"
                            />
                        </div>
                    </div>
                    <hip-spine-x-ray-modal
                        v-if="viewXRays && hipSpineStore.isStudyCompleted"
                        @close-modal="closeModal"
                    />
                    <standing-anteversion-prompt-modal />
                </template>
            </div>

            <div
                class="input-group border-sm rounded-lg pa-4 pt-1"
                :data-analytics-id="PLANNER_TARGETS.cupTranslation"
            >
                <cup-position-controls data-testid="cup-position-controls" />
            </div>

            <div class="actions">
                <app-button-secondary
                    class="w-100"
                    :disabled="vm.isResetCupDisabled"
                    :data-analytics-id="PLANNER_TARGETS.cupResetButton"
                    @click="vm.resetCup"
                    >Reset
                </app-button-secondary>
            </div>
        </div>
    </div>
</template>

<!--suppress CssUnusedSymbol -->
<style scoped>
    .expansion-panel :deep(.v-expansion-panel-title),
    .expansion-panel :deep(.v-expansion-panel-text__wrapper) {
        padding: 0;
    }

    .actions {
        padding: 0 14px;
    }

    .expansion-panel {
        border-radius: 12px;
        width: fit-content;
        height: 100%;
        gap: 10px;
    }

    .cup-panel {
        width: 400px;
        padding: 18px;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;
        scrollbar-gutter: stable both-edges;
        scrollbar-color: rgb(var(--v-theme-on-increment-grey)) #efefef;
    }
</style>
